<template>
    <v-dialog :value="show" width="520" persistent @input="$emit('update:show', $event)">
        <v-card v-if="media">
            <v-card-title class="text-h6">{{ $t('media.library.edit_title') }}</v-card-title>
            <v-divider />
            <v-card-text class="pt-4">
                <div class="me-lbl">{{ $t('media.upload.name') }}</div>
                <v-text-field v-model="form.title" dense outlined hide-details
                    :maxlength="TITLE_MAX" :disabled="isSaving" />

                <div class="me-lbl mt-3">{{ $t('media.upload.desc') }}</div>
                <v-textarea v-model="form.description" dense outlined hide-details rows="2"
                    :maxlength="DESC_MAX" :disabled="isSaving" />

                <div class="me-lbl mt-3">{{ $t('media.upload.scope') }}</div>
                <!-- ⚠ 只有 private / public 兩個選項，沒有 team（2026-08-20 取消）。
                     ⚠ private 的文案**不能寫「只有自己看得到」**（ADR 0007）——
                     物件其實是公開可讀的，只是不會出現在別人的素材庫裡。 -->
                <v-radio-group v-model="form.shareScope" dense hide-details :disabled="isSaving">
                    <v-radio v-for="sc in scopes" :key="sc" :value="sc">
                        <template #label>
                            <div>
                                <div class="me-radio-lbl">{{ $t(`media.upload.scope_${sc}`) }}</div>
                                <div class="me-radio-note">{{ $t(`media.upload.scope_${sc}_note`) }}</div>
                            </div>
                        </template>
                    </v-radio>
                </v-radio-group>

                <!-- 改成 public 就是送審。⚠ **兩句話都要說**（規格 §7.3、ADR 0005）：
                     「經審核後才會成為可用的公開素材」與「你自己仍然可以使用」。
                     只寫「審核中」會讓老師以為東西被鎖住了。 -->
                <div v-if="willSubmitForReview" class="me-review">
                    <div>{{ $t('media.upload.review_notice') }}</div>
                    <div class="me-review-ok">{{ $t('media.upload.review_still_usable') }}</div>
                </div>

                <!-- 已被退回過又要重送：把上一次的理由講出來，否則老師會重蹈覆轍 -->
                <div v-if="wasRejected && media.reject_reason" class="me-reject">
                    {{ $t('media.review.reason', { reason: media.reject_reason }) }}
                </div>

                <div v-if="errorMsg" class="me-err">{{ errorMsg }}</div>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-spacer />
                <v-btn text class="normal-case" :disabled="isSaving" @click="close">
                    {{ $t('button.cancel') }}
                </v-btn>
                <v-btn color="primary" depressed class="normal-case" :loading="isSaving"
                    :disabled="!form.title.trim()" @click="save">
                    {{ $t('button.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mediaServices } from '@/services/media';
import { SHARE_SCOPES } from './mediaConstants';

/// 與後端 UpdateMediaAction 的 StringValidator 上限一致
const TITLE_MAX = 200;
const DESC_MAX = 500;

/**
 * 編輯一筆素材的標題／說明／分享範圍 — 後端 B4 的 `update()` 只收這三個。
 *
 * ⚠ **只有擁有者能改。** 後端對官方素材與別人的素材一律回 403，所以呼叫端要靠
 * `media.is_mine` 決定要不要給入口——不要讓老師按了才拿到權限錯誤。
 *
 * ⚠ **這個元件是共用的**（「我的素材」頁與題目編輯器的素材欄）。分享範圍那段文案有兩條
 * 硬規則（ADR 0005 的送審說明、ADR 0007 的 private 措辭），抽出來就不會兩邊各寫一份、
 * 然後只改到一邊。
 *
 * ⚠ emit `saved` 時帶**整筆**更新後的素材（後端回的形狀與 `QuestMediaBrief` 一致），
 * 呼叫端可以直接拿去覆蓋自己的快照。
 */
export default {
    name: 'MediaEditDialog',
    props: {
        show: { type: Boolean, default: false },
        /** 要編輯的素材。⚠ 呼叫端要先確認 `is_mine`。 */
        media: { type: Object, default: null },
    },
    data() {
        return {
            TITLE_MAX,
            DESC_MAX,
            scopes: SHARE_SCOPES,
            form: { title: '', description: '', shareScope: 'private' },
            isSaving: false,
            errorMsg: '',
        };
    },
    computed: {
        /** 從私有改成公開 = 這次存檔會送審 */
        willSubmitForReview() {
            return this.form.shareScope === 'public'
                && this.media && this.media.share_scope !== 'public';
        },
        wasRejected() {
            return !!(this.media && this.media.review_status === 'rejected');
        },
    },
    watch: {
        /**
         * ⚠ `immediate` 不能省：呼叫端常用 `v-if` 才把這個元件建出來
         * （一個對話框服務多個素材欄），那時掛載當下 `show` 已經是 true，
         * 一般的 watcher 不會觸發，表單就會是空的。
         */
        show: {
            immediate: true,
            handler(opened) {
                if (opened) {
                    this.reset();
                }
            },
        },
    },
    methods: {
        reset() {
            const m = this.media || {};
            this.form = {
                title: m.title || '',
                description: m.description || '',
                shareScope: m.share_scope || 'private',
            };
            this.errorMsg = '';
            this.isSaving = false;
        },

        async save() {
            if (!this.media || !this.form.title.trim()) {
                return;
            }
            this.isSaving = true;
            this.errorMsg = '';
            try {
                const updated = await mediaServices.updateMedia(this.media.media_id, {
                    title: this.form.title.trim(),
                    description: this.form.description.trim() || null,
                    shareScope: this.form.shareScope,
                });
                this.$emit('saved', updated);
                this.$emit('update:show', false);
            } catch (err) {
                /// ⚠ 要分得出 403：那是「這不是你的素材」，跟一般失敗要說的話不一樣。
                this.errorMsg = err && err.status === 403
                    ? this.$t('media.error.forbidden')
                    : this.$t('media.library.save_failed');
            } finally {
                this.isSaving = false;
            }
        },

        close() {
            if (this.isSaving) {
                return;
            }
            this.$emit('update:show', false);
        },
    },
};
</script>

<style scoped>
.me-lbl {
    font-size: 13px;
    font-weight: 600;
    color: #455a64;
    margin-bottom: 4px;
}

.me-radio-lbl {
    font-size: 13px;
    color: #263238;
}

.me-radio-note {
    font-size: 11px;
    color: #90a4ae;
}

.me-review {
    margin-top: 10px;
    padding: 8px 10px;
    background: #e3f2fd;
    border-radius: 4px;
    font-size: 12px;
    color: #37474f;
    line-height: 1.5;
}

.me-review-ok {
    color: #1565c0;
    font-weight: 600;
    margin-top: 2px;
}

.me-reject {
    margin-top: 10px;
    font-size: 12px;
    color: #c62828;
    line-height: 1.5;
}

.me-err {
    font-size: 12px;
    color: #e53935;
    margin-top: 8px;
}
</style>
